import { promisify } from 'util';
import fs from 'fs';
import path from 'path';
import SyncGlobalRepo from '@/renderer/scripts/CoSync/SyncCenter/Repo/SyncGlobalRepo';
import smh from '@/renderer/scripts/smh';
import {
  CoSyncInfo,
  CoSyncStrategy,
  CoSyncTaskConfig,
  getLocalPathUserChoose,
} from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncConfig';
import {
  CreatePersonalSyncParams,
  CreateTeamSyncParams,
  QuerySyncInfoData,
  QuerySyncInfoParams,
  SpaceTag,
  SyncMode,
} from '@/renderer/scripts/libs/smh-sdk-class/typings';
import { getDeviceName } from '@/renderer/scripts/utils/tools';
import { isFinderAlias } from '@/common/listFolders';

const requireNotSymbolicLink = (stats: fs.Stats) => {
  if (stats.isSymbolicLink()) {
    logger.ui.info('local requireNotSymbolicLink SymbolicLinkNotSupported');
    throw { code: 'SymbolicLinkNotSupported' };
  }
};

const requireNotFinderAlias = (path: string) => {
  const isAlias = isFinderAlias(path);
  if (isAlias) {
    logger.ui.info('local requireNotFinderAlias NotSuportFinderAlias');
    throw { code: 'NotSuportFinderAlias' };
  }
};

const requireIsDirectory = (stats: fs.Stats) => {
  if (!stats.isDirectory()) {
    logger.ui.info('local requireIsDirectory SymbolicLinkOrFileAreNotSupported');
    throw { code: 'SymbolicLinkOrFileAreNotSupported' };
  }
};

const requireWithINode = (stats: fs.Stats) => {
  if (stats.ino === 0) {
    logger.ui.info('local requireWithINode NoINoIsNotSupport');
    throw { code: 'NoINoIsNotSupport' };
  }
};

/**
 * @description 同步服务的配置确认器
 * @export
 * @class ConfigConfirmer
 */
export default class ConfigConfirmer {
  private readonly syncGlobalRepo: SyncGlobalRepo;

  constructor(syncGlobalRepo: SyncGlobalRepo) {
    this.syncGlobalRepo = syncGlobalRepo;
  }

  /**
   * @description 新的配置是否可以加入
   * @param {CoSyncTaskConfig} config
   * @return {*}  {Promise<number>}
   * @memberof ConfigConfirmer
   */
  public async checkIfAppendable(config: CoSyncTaskConfig): Promise<number> {
    const existConfigList = await this.syncGlobalRepo.getAllConfig();
    if (existConfigList.length >= 5) {
      logger.ui.info('checkIfAppendable SyncTaskLimit5');
      return Promise.reject({ code: 'SyncTaskLimit5' });
    }

    await this.checkLocalPath(config, existConfigList);

    // 如果本地目录不存在，尝试创建
    const needCreateLocalFolder = !fs.existsSync(config.syncPath.localPath);
    if (needCreateLocalFolder) {
      await this.createDir(config.syncPath.localPath);
    }

    try {
      const syncId = await this.checkRemotePath(config);
      return syncId;
    } catch (error) {
      if (needCreateLocalFolder) {
        await this.removeDir(config.syncPath.localPath);
      }
      throw error;
    }
  }

  private async createDir(path: string) {
    try {
      await promisify(fs.mkdir)(path, { recursive: true });
    } catch (error) {
      logger.ui.error('create local folder failed', error);
      throw { code: 'CreateLocalFolderFailed' };
    }
  }

  private async removeDir(path: string) {
    try {
      await promisify(fs.rmdir)(path);
    } catch (error) {
      logger.ui.error('remote local folder failed', error);
      // 移除失败不抛移除，只打日志，避免覆盖底层的失败原因
    }
  }

  private async checkLocalIsExistSameTask(newConfig: CoSyncTaskConfig, existConfigList: CoSyncInfo[]): Promise<void> {
    const existSameTask = existConfigList.some(
      item =>
        item.config.syncPath.localPath === newConfig.syncPath.localPath &&
        item.config.syncPath.remotePath === newConfig.syncPath.remotePath &&
        item.config.syncPath.spaceId === newConfig.syncPath.spaceId,
    );
    if (existSameTask) {
      logger.ui.info('checkLocalIsExistSameTask existSameTask');
      throw { code: 'SyncTaskExist' };
    }
  }

  private async checkLocalIsExistSameLocalFolder(
    newConfig: CoSyncTaskConfig,
    existConfigList: CoSyncInfo[],
  ): Promise<void> {
    const existSameLocal = existConfigList.some(item => {
      const newChoiceLocalPath = newConfig.syncPath.localPath;
      const itemLocalPath = item.config.syncPath.localPath;

      return itemLocalPath === newChoiceLocalPath;
    });
    if (existSameLocal) {
      logger.ui.info('checkLocalIsExistSameLocalFolder SameLocalFolderExist');
      throw { code: 'SameLocalFolderExist' };
    }
  }

  private async checkLocalIncluded(newConfig: CoSyncTaskConfig, existConfigList: CoSyncInfo[]): Promise<void> {
    // 新路径是否被其他同步盘包含
    // 已有A/B/C
    // 新创建 A/B
    const existChild = existConfigList.some(item => {
      const newChoiceLocalPath = newConfig.syncPath.localPath;
      const newChoiceFolderLocalPath = path.join(newChoiceLocalPath, path.sep);
      const itemLocalPath = item.config.syncPath.localPath;
      return itemLocalPath.includes(newChoiceFolderLocalPath);
    });
    if (existChild) {
      logger.ui.info('checkLocalIncluded SyncChildFolderExist');
      throw { code: 'SyncChildFolderExist' };
    }

    // 新路径是否包含其他同步盘
    // 已有A/B
    // 新创建 A/B/C
    const existParent = existConfigList.some(item => {
      const newChoiceLocalPath = newConfig.syncPath.localPath;
      const itemLocalPath = item.config.syncPath.localPath;
      const folderLocalPath = path.join(itemLocalPath, path.sep);

      return newChoiceLocalPath.includes(folderLocalPath);
    });
    if (existParent) {
      logger.ui.info('checkLocalIncluded SyncParentFolderExist');
      throw { code: 'SyncParentFolderExist' };
    }
  }

  private async checkLocalAccessPermission(newConfig: CoSyncTaskConfig, existConfigList: CoSyncInfo[]): Promise<void> {
    const localPathUserChoose = getLocalPathUserChoose(newConfig.syncPath);
    const existed = await promisify(fs.exists)(localPathUserChoose);
    if (!existed) {
      return;
    }

    try {
      await promisify(fs.access)(localPathUserChoose, fs.constants.R_OK | fs.constants.W_OK);
    } catch (error: MsgError) {
      logger.ui.warn('checkLocalAccessPermission', error);
      throw { code: 'NoOperatePermission' };
    }
  }

  private async checkLocalFileInfo(newConfig: CoSyncTaskConfig, existConfigList: CoSyncInfo[]): Promise<void> {
    let stats: fs.Stats | null = null;
    try {
      // 不存在的话不需要检测是否是快捷方式
      const isLocalFolderExist = await promisify(fs.exists)(newConfig.syncPath.localPath);
      if (!isLocalFolderExist) return;

      // 此处只是用于判断，可不返回bigint类型
      stats = await promisify(fs.lstat)(newConfig.syncPath.localPath);
    } catch (error: MsgError) {
      // 获取信息失败的话，不要影响创建，后续执行会有兜底
      logger.ui.warn('fs.lstat error', error);
      return;
    }

    if (!stats) return;

    // 要求不是 alias 替身文件/夹
    requireNotFinderAlias(newConfig.syncPath.localPath);
    // 要求不是快捷方式（Mac上检测不出alias）
    requireNotSymbolicLink(stats);
    // 要求是目录（可以覆盖alias）
    requireIsDirectory(stats);
    // 要求是有inode
    requireWithINode(stats);
  }

  private checkLocalExtName(newConfig: CoSyncTaskConfig): void {
    const extName = path.extname(newConfig.syncPath.localPath).toLowerCase();
    if (extName === '.app') {
      logger.ui.info('checkLocalExtName AppFileNotSupported');
      throw { code: 'AppFileNotSupported' };
    }
  }

  /**
   * @description 检测本地文件夹是否可以建立同步盘
   * @private
   * @param {CoSyncTaskConfig} newChoice
   * @param {CoSyncInfo[]} existConfigList
   * @return {*}
   * @memberof ConfigConfirmer
   */
  private async checkLocalPath(newConfig: CoSyncTaskConfig, existConfigList: CoSyncInfo[]): Promise<void> {
    // 检测是否存在相同的同步任务
    await this.checkLocalIsExistSameTask(newConfig, existConfigList);
    // 检测是否存在相同的本地文件夹
    await this.checkLocalIsExistSameLocalFolder(newConfig, existConfigList);
    // 检测是否存在本地路径包含关系
    await this.checkLocalIncluded(newConfig, existConfigList);
    // 检测读写权限
    await this.checkLocalAccessPermission(newConfig, existConfigList);
    // 检测快捷方式
    await this.checkLocalFileInfo(newConfig, existConfigList);
    // 检测文件类型
    this.checkLocalExtName(newConfig);
  }

  /**
   * @description 检测远端路径是否可以创建同步盘
   * @private
   * @param {CoSyncTaskConfig} config
   * @return {*}  {Promise<number>}
   * @memberof ConfigConfirmer
   */
  private async checkRemotePath(config: CoSyncTaskConfig): Promise<number> {
    const { spaceId, localPath, remotePath } = config.syncPath;

    try {
      await smh.commonAPI.createDirectory({ dirPath: remotePath, spaceId });
    } catch (err: MsgError) {
      // 创建失败不处理，createSync会检查，避免权限问题
      logger.ui.error('checkRemotePath error', err);
    }

    if (config.syncPath.spaceTag !== SpaceTag.team && config.syncPath.spaceTag !== SpaceTag.personal) {
      return Promise.reject({ code: 'NotSupportSpaceType' });
    }

    let mode: SyncMode = SyncMode.TwoWay;
    switch (config.syncSetting.strategy) {
      case CoSyncStrategy.BOTH:
        mode = SyncMode.TwoWay;
        break;
      case CoSyncStrategy.CLOUD_TO_LOCAL:
        mode = SyncMode.CloudToLocal;
        break;
      case CoSyncStrategy.LOCAL_TO_CLOUD:
        mode = SyncMode.LocalToCloud;
        break;

      default: {
        const _exhaustiveCheck: never = config.syncSetting.strategy;
        break;
      }
    }

    const deviceName = getDeviceName();
    const createPersonalSyncParam: CreatePersonalSyncParams = {
      spaceTag: SpaceTag.personal,
      cloudPath: remotePath,
      localPath,
      clientId: deviceName,
      mode,
    };

    const createTeamSyncParams: CreateTeamSyncParams = {
      spaceTag: SpaceTag.team,
      teamId: config.syncPath.teamId,
      cloudPath: remotePath,
      localPath,
      clientId: deviceName,
      mode,
    };

    const detailParam: CreatePersonalSyncParams | CreateTeamSyncParams =
      config.syncPath.spaceTag === SpaceTag.personal ? createPersonalSyncParam : createTeamSyncParams;

    const { data } = await smh.userAPI.createSync(detailParam);
    return data.syncId;
  }

  public async queryRemoteSyncInfo(coSyncInfoList: CoSyncInfo[]): Promise<QuerySyncInfoData[]> {
    if (coSyncInfoList.length <= 0) return [];

    const param: QuerySyncInfoParams = {
      id_list: coSyncInfoList.map(it => it.syncId).join(','),
    };

    const { data } = await smh.userAPI.querySyncInfo(param);
    return data;
  }
}
